import {
  CHAT_REPORT_CATEGORIES,
  type ChatMessage as ChatMessageData,
  type ChatReportCategory,
  MAX_CHAT_NOTE_LENGTH,
} from "@loot-os/api-contract";
import { Menu, MenuItem, MenuSeparator } from "@loot-os/design-system/core/Menu";
import { ChatDrawer } from "@loot-os/design-system/domain/chat/ChatDrawer";
import { ChatIgnoredModal } from "@loot-os/design-system/domain/chat/ChatIgnoredModal";
import { ChatMessage } from "@loot-os/design-system/domain/chat/ChatMessage";
import { ChatMessageActions } from "@loot-os/design-system/domain/chat/ChatMessageActions";
import { ChatPanel } from "@loot-os/design-system/domain/chat/ChatPanel";
import { ChatReplyBar } from "@loot-os/design-system/domain/chat/ChatReplyBar";
import { ChatReportModal } from "@loot-os/design-system/domain/chat/ChatReportModal";
import { ChatSystemMessage } from "@loot-os/design-system/domain/chat/ChatSystemMessage";
import { ChatTranscript } from "@loot-os/design-system/domain/chat/ChatTranscript";
import copyIcon from "@loot-os/icons/icons/copy";
import eyeIcon from "@loot-os/icons/icons/eye";
import eyeOffIcon from "@loot-os/icons/icons/eye-off";
import flagIcon from "@loot-os/icons/icons/flag";
import trashIcon from "@loot-os/icons/icons/trash";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  DEFAULT_CHAT_ROOM,
  mentionsViewer,
  setChatOpen,
  toUserFacingChatError,
  useChatHeartbeat,
  useChatOpen,
  useChatRoom,
  useDeleteChatMessage,
  useIgnoredUsers,
  useReportChatMessage,
} from "../../lib/chat.js";
import { useProfile } from "../../lib/profile.js";
import { lootMeQueryOptions } from "../../lib/queries.js";
import { useShopper } from "../../lib/shopper.js";
import { StoreChatComposer } from "./StoreChatComposer.js";

const REPORT_REASONS: readonly { value: ChatReportCategory; label: string }[] =
  CHAT_REPORT_CATEGORIES.map((value) => ({
    value,
    label: {
      HARASSING: "Harassment",
      INAPPROPRIATE: "Inappropriate content",
      SPAM: "Spam",
      SUSPICIOUS: "Suspicious activity",
    }[value],
  }));

/** How long a message stays marked after a reply's quote jumps to it. */
const HIGHLIGHT_MS = 3_000;

function timeLabel(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime())
    ? ""
    : at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/**
 * The store's chat: the design system's ChatDrawer and ChatPanel, filled from the room. Mounted
 * once beside the routes while the store has chat. The signed-in shopper's presence beats
 * whether or not the drawer is open; the room itself is subscribed only while it is. Closing it
 * returns focus to whatever opened it.
 */
export function StoreChat() {
  const open = useChatOpen();
  const { user, sessionToken } = useShopper();
  useChatHeartbeat(DEFAULT_CHAT_ROOM, !!user && !!sessionToken);

  const opener = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current && document.activeElement instanceof HTMLElement) {
      opener.current = document.activeElement;
    }
    if (!open && wasOpen.current) {
      opener.current?.focus();
      opener.current = null;
    }
    wasOpen.current = open;
  }, [open]);

  return (
    <ChatDrawer open={open} onOpenChange={setChatOpen} label="Chat">
      <ChatRoom />
    </ChatDrawer>
  );
}

function ChatRoom() {
  const roomId = DEFAULT_CHAT_ROOM;
  const { user, sessionToken } = useShopper();
  const room = useChatRoom(roomId);
  const me = useQuery({ ...lootMeQueryOptions(), enabled: !!sessionToken });
  const { ignored, isIgnored, ignore, unignore } = useIgnoredUsers();
  const remove = useDeleteChatMessage(roomId);
  const report = useReportChatMessage(roomId);
  const { showProfile } = useProfile();

  const [replyingTo, setReplyingTo] = useState<ChatMessageData | null>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const [reporting, setReporting] = useState<ChatMessageData | null>(null);
  const [reportSent, setReportSent] = useState(false);
  const [reportError, setReportError] = useState<string | undefined>(undefined);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [ignoredOpen, setIgnoredOpen] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>(undefined);
  const [highlighted, setHighlighted] = useState<string | null>(null);

  useEffect(() => {
    if (!highlighted) return;
    const timer = setTimeout(() => setHighlighted(null), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [highlighted]);

  const jumpTo = useCallback((messageId: string) => {
    const target = document.getElementById(`chat-message-${messageId}`);
    if (!target) return;
    target.scrollIntoView({ block: "center", behavior: "smooth" });
    setHighlighted(messageId);
  }, []);

  const signedIn = !!user;
  const viewerId = me.data?.id ?? null;
  const username = me.data?.username ?? null;
  const headcount = room.data?.headcount ?? 0;

  const deleteMessage = (message: ChatMessageData) => {
    setActionError(undefined);
    remove.mutate(message.messageId, {
      onError: (err) => setActionError(toUserFacingChatError(err)),
    });
  };
  const closeReport = () => {
    setReporting(null);
    setReportSent(false);
    setReportError(undefined);
    report.reset();
  };

  const visible = room.data?.messages.filter((m) => !isIgnored(m.endUserId)) ?? [];

  return (
    <>
      <ChatPanel
        title="Chat"
        online={{
          count: headcount.toLocaleString(),
          label: `${headcount.toLocaleString()} online`,
        }}
        close={{ label: "Close chat", onClick: () => setChatOpen(false) }}
        replyBar={
          replyingTo && (
            <ChatReplyBar
              label="Replying to"
              name={replyingTo.displayName}
              text={replyingTo.text ?? ""}
              cancelLabel="Cancel reply"
              onCancel={() => setReplyingTo(null)}
            />
          )
        }
        composer={
          <StoreChatComposer
            roomId={roomId}
            username={username}
            profileLoaded={me.isSuccess}
            muted={room.data?.viewer?.muted ?? false}
            mutedUntil={room.data?.viewer?.mutedUntil}
            replyingTo={replyingTo}
            onSent={() => setReplyingTo(null)}
            actionError={actionError}
            rulesOpen={rulesOpen}
            onRulesOpenChange={setRulesOpen}
            onOpenIgnored={() => setIgnoredOpen(true)}
            inputRef={composerRef}
          />
        }
      >
        <ChatTranscript label="Chat messages" jumpLabel="New messages">
          {room.isPending ? (
            <ChatSystemMessage>Loading…</ChatSystemMessage>
          ) : room.isError ? (
            <ChatSystemMessage>{toUserFacingChatError(room.error)}</ChatSystemMessage>
          ) : visible.length === 0 ? (
            <ChatSystemMessage>Welcome to chat. Say hello!</ChatSystemMessage>
          ) : (
            visible.map((message) => {
              const own = viewerId !== null && message.endUserId === viewerId;
              return (
                <div key={message.messageId} id={`chat-message-${message.messageId}`}>
                  <ChatMessage
                    author={{ name: message.displayName }}
                    text={message.text ?? ""}
                    time={{ label: timeLabel(message.createdAt), dateTime: message.createdAt }}
                    mine={own}
                    mentioned={!own && !!message.text && mentionsViewer(message.text, username)}
                    deleted={message.deleted ? { label: "Message deleted" } : undefined}
                    replyTo={
                      message.replyTo && {
                        name: message.replyTo.displayName,
                        text: message.replyTo.deleted
                          ? "Message deleted"
                          : (message.replyTo.text ?? ""),
                        onJump: () => jumpTo(message.replyTo?.messageId ?? ""),
                        jumpLabel: "Show the message it answers",
                      }
                    }
                    onAuthorClick={() => showProfile(message.endUserId)}
                    className={highlighted === message.messageId ? "bg-accent-subtle" : undefined}
                    actions={
                      !message.deleted && (
                        <ChatMessageActions
                          reply={
                            signedIn
                              ? {
                                  label: `Reply to ${message.displayName}`,
                                  onClick: () => {
                                    setReplyingTo(message);
                                    // Straight into the message box: Reply, then type.
                                    composerRef.current?.focus();
                                  },
                                }
                              : undefined
                          }
                          more={
                            <Menu
                              trigger={
                                <ChatMessageActions.MoreButton
                                  label={`Actions for ${message.displayName}'s message`}
                                />
                              }
                            >
                              {message.text && (
                                <MenuItem
                                  icon={copyIcon}
                                  onClick={() => {
                                    void navigator.clipboard
                                      ?.writeText(message.text ?? "")
                                      .catch(() => undefined);
                                  }}
                                >
                                  Copy text
                                </MenuItem>
                              )}
                              <MenuSeparator />
                              {own ? (
                                <MenuItem
                                  icon={trashIcon}
                                  danger
                                  onClick={() => deleteMessage(message)}
                                >
                                  Delete
                                </MenuItem>
                              ) : (
                                <>
                                  <MenuItem
                                    icon={isIgnored(message.endUserId) ? eyeIcon : eyeOffIcon}
                                    onClick={() =>
                                      isIgnored(message.endUserId)
                                        ? unignore(message.endUserId)
                                        : ignore({
                                            endUserId: message.endUserId,
                                            displayName: message.displayName,
                                          })
                                    }
                                  >
                                    {isIgnored(message.endUserId)
                                      ? "Unignore player"
                                      : "Ignore player"}
                                  </MenuItem>
                                  {signedIn && (
                                    <MenuItem
                                      icon={flagIcon}
                                      danger
                                      onClick={() => setReporting(message)}
                                    >
                                      Report
                                    </MenuItem>
                                  )}
                                </>
                              )}
                            </Menu>
                          }
                        />
                      )
                    }
                  />
                </div>
              );
            })
          )}
        </ChatTranscript>
      </ChatPanel>

      <ChatReportModal<ChatReportCategory>
        open={reporting !== null}
        onOpenChange={(next) => !next && closeReport()}
        title="Report message"
        description={reporting ? `From ${reporting.displayName}. Staff will review it.` : undefined}
        reasonLabel="Reason"
        reasons={REPORT_REASONS}
        noteLabel="Details (optional)"
        noteMaxLength={MAX_CHAT_NOTE_LENGTH}
        submitLabel="Send report"
        cancelLabel="Cancel"
        submitting={report.isPending}
        error={reportError}
        sent={
          reportSent
            ? { message: "Thanks. The report was sent to staff.", doneLabel: "Done" }
            : undefined
        }
        onSubmit={({ reason, note }) => {
          if (!reporting) return;
          setReportError(undefined);
          report.mutate(
            { messageId: reporting.messageId, category: reason, description: note || undefined },
            {
              onSuccess: () => setReportSent(true),
              onError: (err) => setReportError(toUserFacingChatError(err)),
            },
          );
        }}
      />
      <ChatIgnoredModal
        open={ignoredOpen}
        onOpenChange={setIgnoredOpen}
        title="Ignored players"
        description="Their messages are hidden on this device only. They are not told."
        users={ignored.map((u) => ({ id: u.endUserId, name: u.displayName }))}
        emptyLabel="You are not ignoring anyone."
        unignoreLabel="Unignore"
        unignoreName={(name) => `Unignore ${name}`}
        onUnignore={unignore}
        doneLabel="Done"
      />
    </>
  );
}
